import { useEffect, useRef, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Hint } from "@/components/devicePanel/Hint";
import { ResearchBanner, ResearchTag } from "@/components/devicePanel/ResearchTag";
import {
  LIST_HEADER,
  STUDY_CELL as CELL,
  STUDY_EDGE_CELL,
  STUDY_HEAD_ROW,
  studyHeadCell,
} from "@/components/devicePanel/panelLayout";
import { usePrefetchDevicePanelStudy } from "@/hooks/useDevicePanel";
import { useAuth } from "@/context/AuthContext";
import { isFlagged, siteStatus, type SiteStatus } from "@/types/notificationView";
import {
  callLine,
  listReading,
  qualifiedName,
  unreadTriage,
  type DevicePanelStudiesResponse,
  type DevicePanelStudyRow,
  type DevicePanelTriageFinding,
} from "@/types/devicePanel";

type Panel = DevicePanelStudiesResponse["panel"];
type Devices = Panel["devices"];

/** The filters every panel worklist offers; a view may add its own after them. */
export const STUDY_FILTERS = { All: () => true, Flagged: isFlagged } as const;
export type StudyFilter = keyof typeof STUDY_FILTERS;

// State cues follow AGENTS.md "State encoding is non-hue": a flagged study gets
// the attention rule on its row edge and its findings; any other status is plain
// text in the findings cell.

/** A study's state when nothing is flagged: no flag, not sent (with why), or no
 * readable result yet. */
function StatusText({ status }: { status: Exclude<SiteStatus, { kind: "flagged" }> }) {
  switch (status.kind) {
    case "no_flag":
      return <span className="text-fg-muted">No flag</span>;
    case "not_sent":
      return <span className="text-fg-dim">Not sent · {status.reasons.join(" · ")}</span>;
    case "unread":
      return <span className="text-fg-muted">{status.text}</span>;
  }
}

/** A flagged finding by its grouped name. Which devices flagged it, each with
 * what its call means and in its own wording, shows on hover and focus. */
function FindingChip({
  finding,
  devices,
}: {
  finding: DevicePanelTriageFinding;
  devices: Devices;
}) {
  const lines = finding.devices.map((flag) => {
    const device = devices.find((candidate) => candidate.key === flag.key);
    if (!device) throw new Error(`Finding ${finding.key} names unknown device ${flag.key}`);
    const name = qualifiedName(device.short, flag.qualifier) ?? device.short;
    return { key: flag.key, text: callLine(name, flag.display_name, listReading(flag)) };
  });
  return (
    <Hint
      label={
        <span className="grid gap-0.5">
          {lines.map(({ key, text }) => (
            <span key={key}>{text}</span>
          ))}
        </span>
      }
    >
      <li
        tabIndex={0}
        className="rounded-xs bg-surface-2 px-2 py-0.5 text-[12.5px] text-fg focus-visible:outline focus-visible:outline-1 focus-visible:outline-signal"
      >
        {finding.display_name}
      </li>
    </Hint>
  );
}

/** The study's flagged findings, or its state when nothing is flagged, with a
 * note for each in-scope device that has no result yet. */
function FindingsCell({
  row,
  status,
  devices,
}: {
  row: DevicePanelStudyRow;
  status: SiteStatus;
  devices: Devices;
}) {
  return (
    <ul aria-label="Suspected findings" className="flex flex-wrap items-center gap-1.5 text-[12px]">
      {status.kind === "flagged" ? (
        row.triage_findings.map((finding) => (
          <FindingChip key={finding.key} finding={finding} devices={devices} />
        ))
      ) : (
        <li>
          <StatusText status={status} />
        </li>
      )}
      {unreadTriage(row, devices).map((note) => (
        <li key={note} className="font-mono text-[11px] text-fg-dim">
          {note}
        </li>
      ))}
    </ul>
  );
}

/** The panel's devices by full name, with their status in the notification view
 * (a site's own worklist). A research view, such as one a partner reviews, names
 * them only: a status there can describe a submission still under review. */
function DeviceNames({ panel }: { panel: Panel }) {
  return (
    <p aria-label="Devices" className="text-[11px] text-fg-muted">
      {panel.devices
        .map((device) =>
          panel.view === "notification" ? `${device.label} (${device.status_label})` : device.label,
        )
        .join(" · ")}
    </p>
  );
}

/**
 * A panel worklist's header: its research marker (a banner over a notification
 * view, a tag in a research view), name, devices, counts and filters, with a
 * view's own `actions` at the right edge.
 */
export function PanelListHeader<F extends string>({
  panel,
  studies,
  filters,
  filter,
  onFilter,
  actions,
}: {
  panel: Panel;
  studies: readonly DevicePanelStudyRow[];
  filters: readonly F[];
  filter: F;
  onFilter: (filter: F) => void;
  actions?: ReactNode;
}) {
  const notSent = studies.filter((row) => siteStatus(row).kind === "not_sent").length;
  return (
    <>
      {panel.view === "notification" && <ResearchBanner label={panel.research_label} />}
      <header className={LIST_HEADER}>
        <h1 className="text-sm font-medium">{panel.name}</h1>
        {panel.view === "research" && <ResearchTag label={panel.research_label} />}
        <DeviceNames panel={panel} />
        <span className="font-mono text-[11px] text-fg-dim tabular-nums">
          {[
            `${studies.length} studies`,
            `${studies.filter(isFlagged).length} flagged`,
            ...(notSent ? [`${notSent} not sent`] : []),
          ].join(" · ")}
        </span>
        {/* Actions stay at the right edge; the filters open to their left. */}
        <div className="ml-auto flex items-center gap-3">
          <div role="group" aria-label="Show studies" className="flex gap-1">
            {filters.map((name) => (
              <button
                key={name}
                type="button"
                aria-pressed={filter === name}
                onClick={() => onFilter(name)}
                className="rounded-xs px-2 py-0.5 text-[11.5px] text-fg-muted hover:text-fg aria-pressed:bg-surface-2 aria-pressed:text-fg"
              >
                {name}
              </button>
            ))}
          </div>
          {actions}
        </div>
      </header>
    </>
  );
}

/** A worklist column: its heading, when it applies to a set of rows (always
 * when unset), its alignment, its cell's classes and content. */
interface Column {
  heading: string;
  shown?: (rows: readonly DevicePanelStudyRow[]) => boolean;
  align?: "right";
  className: string;
  cell: (row: DevicePanelStudyRow, cell: CellContext) => ReactNode;
}

interface CellContext {
  status: SiteStatus;
  devices: Devices;
  url: string;
}

/** A column a view adds after the findings, at the row's right edge: its report
 * draft's count, or its review progress. */
export type ExtraColumn = Pick<Column, "heading" | "cell">;

/** A column shows only where it tells the rows apart or carries data. */
const distinguishes =
  (value: (row: DevicePanelStudyRow) => unknown) => (rows: readonly DevicePanelStudyRow[]) =>
    new Set(rows.map(value)).size > 1;

/** The worklist's columns. */
export const STUDY_COLUMNS: readonly Column[] = [
  {
    heading: "Case",
    className: STUDY_EDGE_CELL,
    cell: (row, { url }) => (
      <Link
        to={url}
        onClick={(event) => event.stopPropagation()}
        className="font-mono text-[12px] text-fg focus-visible:outline focus-visible:outline-1 focus-visible:outline-signal"
      >
        {row.label}
      </Link>
    ),
  },
  {
    heading: "Region",
    shown: distinguishes((row) => row.region),
    className: `${CELL} whitespace-nowrap font-mono text-[11px] text-fg-muted`,
    cell: (row) => row.region,
  },
  {
    heading: "Exam",
    shown: (rows) => rows.some((row) => row.exam !== null),
    className: `${CELL} max-w-64 truncate font-medium`,
    cell: (row) => <span title={row.exam ?? undefined}>{row.exam ?? "—"}</span>,
  },
  {
    heading: "Age",
    shown: (rows) => rows.some((row) => row.age_years !== null || row.age_recorded === false),
    className: `${CELL} font-mono text-[11px] tabular-nums`,
    cell: (row) =>
      row.age_recorded === false ? (
        <span className="text-fg-dim">not recorded</span>
      ) : (
        (row.age_years ?? "—")
      ),
  },
  {
    heading: "Suspected findings",
    className: `${CELL} w-full`,
    cell: (row, { status, devices }) => (
      <FindingsCell row={row} status={status} devices={devices} />
    ),
  },
];

/** How long the pointer rests on a row before its study loads ahead: sweeping
 * across the list loads nothing, pausing on a row does. */
const PREFETCH_DWELL_MS = 150;

const EXTRA_CELL = `${STUDY_EDGE_CELL} whitespace-nowrap text-right font-mono text-[11px] text-fg-muted tabular-nums`;

function StudyRow({
  row,
  columns,
  devices,
  url,
  onIntent,
}: {
  row: DevicePanelStudyRow;
  columns: readonly Column[];
  devices: Devices;
  url: string;
  onIntent: () => void;
}) {
  const navigate = useNavigate();
  const status = siteStatus(row);
  const dwell = useRef<number>(undefined);
  useEffect(() => () => window.clearTimeout(dwell.current), []);
  return (
    <tr
      data-testid={`study-row-${row.study_uid}`}
      onClick={() => navigate(url)}
      onPointerEnter={() => {
        dwell.current = window.setTimeout(onIntent, PREFETCH_DWELL_MS);
      }}
      onPointerLeave={() => window.clearTimeout(dwell.current)}
      onFocus={onIntent}
      className={`cursor-pointer border-b border-l-2 border-border text-xs hover:bg-surface-2 ${
        status.kind === "flagged" ? "border-l-border-strong" : "border-l-transparent"
      }`}
    >
      {columns.map((column) => (
        <td key={column.heading} className={column.className}>
          {column.cell(row, { status, devices, url })}
        </td>
      ))}
    </tr>
  );
}

/**
 * A panel's studies as a worklist, in the order given, in the columns that
 * apply to them. Every panel view lists its studies here; each row opens
 * `caseUrl`, and resting the pointer on a row (or focusing it) loads its study ahead so the case opens on
 * cached output.
 */
export function StudyTable({
  panelId,
  rows,
  devices,
  caseUrl,
  extra,
}: {
  panelId: string;
  rows: readonly DevicePanelStudyRow[];
  devices: Devices;
  caseUrl: (row: DevicePanelStudyRow) => string;
  extra?: ExtraColumn;
}) {
  const { token } = useAuth();
  const prefetch = usePrefetchDevicePanelStudy(token, panelId);
  const columns = [
    ...STUDY_COLUMNS.filter((column) => column.shown?.(rows) ?? true),
    ...(extra ? [{ ...extra, align: "right" as const, className: EXTRA_CELL }] : []),
  ];
  return (
    <table className="w-full border-collapse text-left">
      <thead className="sticky top-0 bg-bg">
        <tr className={STUDY_HEAD_ROW}>
          {columns.map((column, index) => (
            <th key={column.heading} className={studyHeadCell(index, column.align)}>
              {column.heading}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <StudyRow
            key={row.study_uid}
            row={row}
            columns={columns}
            devices={devices}
            url={caseUrl(row)}
            onIntent={() => prefetch(row.study_uid)}
          />
        ))}
      </tbody>
    </table>
  );
}
